import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Kanban } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["planning", "in_progress", "on_hold", "completed", "cancelled"];
const KINDS = ["website", "mobile_app", "seo", "uniform", "nfc", "generic"];
const KIND_TITLES = {
  website: "Website projects", mobile_app: "Mobile app projects",
  seo: "SEO projects", uniform: "Uniform projects", nfc: "NFC projects",
  generic: "General projects",
};
const empty = { name: "", client_id: "", kind: "generic", status: "planning", start_date: "", end_date: "", budget: 0, description: "" };

export default function Projects() {
  const { t } = useLang();
  const { kind: kindParam } = useParams();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/projects"), api.get("/crm/clients").catch(() => ({ data: [] }))]);
    setItems(p.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = kindParam ? items.filter((p) => p.kind === kindParam) : items;
  const pageTitle = kindParam ? (KIND_TITLES[kindParam] || t("projects.title")) : t("projects.title");

  const submit = async () => {
    try {
      const payload = { ...form, budget: Number(form.budget || 0) };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      if (editing) await api.patch(`/projects/${editing.project_id}`, payload);
      else await api.post("/projects", payload);
      toast.success(t("common.save"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm(t("common.confirm") + "?")) return;
    try { await api.delete(`/projects/${id}`); toast.success(t("common.delete")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout title={pageTitle} subtitle={t("projects.subtitle")}
      right={
        <div className="flex items-center gap-2">
          <Link data-testid="projects-to-kanban" to="/app/projects/kanban" className="btn-scale h-9 px-3 rounded-md border border-slate-700 text-slate-300 text-sm inline-flex items-center gap-2 hover:bg-slate-800">
            <Kanban className="w-4 h-4" /> Kanban view
          </Link>
          <button data-testid="projects-new" onClick={() => { setEditing(null); setForm({ ...empty, kind: kindParam || "generic" }); setOpen(true); }} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400">
            <Plus className="w-4 h-4" /> {t("projects.new")}
          </button>
        </div>
      }
    >
      <div className="rounded-xl border border-zinc-800 overflow-x-auto" data-testid="projects-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3">Project</th><th className="p-3">Kind</th><th className="p-3">Client</th>
              <th className="p-3">Budget</th><th className="p-3">Status</th><th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.project_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{p.name}</td>
                <td className="p-3 text-zinc-400 text-xs">{t(`projects.kinds.${p.kind}`)}</td>
                <td className="p-3 text-zinc-400 text-xs">{clients.find((c) => c.client_id === p.client_id)?.name || "—"}</td>
                <td className="p-3 text-zinc-400">${Number(p.budget || 0).toFixed(0)}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full ${p.status === "in_progress" ? "bg-blue-500 glow-green" : p.status === "completed" ? "bg-emerald-500" : "bg-zinc-500"}`} />
                    {t(`projects.statuses.${p.status}`)}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`project-edit-${p.project_id}`} onClick={() => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); }} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-blue-300 hover:bg-zinc-800 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`project-del-${p.project_id}`} onClick={() => remove(p.project_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800 inline-flex"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">{t("common.no_data")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl" data-testid="project-dialog">
          <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("projects.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <label><span className="overline block mb-1.5">Name</span>
              <input data-testid="project-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">Client</span>
              <select data-testid="project-field-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                <option value="">—</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Kind</span>
              <select data-testid="project-field-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {KINDS.map((k) => <option key={k} value={k}>{t(`projects.kinds.${k}`)}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Status</span>
              <select data-testid="project-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{t(`projects.statuses.${s}`)}</option>)}
              </select>
            </label>
            <label><span className="overline block mb-1.5">Budget ($)</span>
              <input data-testid="project-field-budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">Start date</span>
              <input data-testid="project-field-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label><span className="overline block mb-1.5">End date</span>
              <input data-testid="project-field-end" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            </label>
            <label className="md:col-span-2"><span className="overline block mb-1.5">Description</span>
              <textarea data-testid="project-field-description" rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="project-save" onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
